<template>
    <section class="px-4 py-4 mx-auto max-w-4xl md:py-4 lg:px-4" :class="newStyle ? 'md-max:pt-0 -mt-4' : ''">
        <div class="">
            <!-- Title -->
            <div class="mb-12 text-center md:mb-16">
                <h3 class="font-bold text-gray-900 font-ww-bold"
                    :class="newStyle ? 'text-4xl' : 'md:text-3xl xl:text-4xl text-2xl'" v-html="mainTitle">
                </h3>
            </div>

            <!-- List Items -->
            <div class="flex flex-col gap-12 mx-auto max-w-5xl" :class="newStyle ? 'lg:px-0 px-10' : ''">
                <div v-for="(block, index) in blocks" :key="index"
                    class="flex flex-col gap-y-4 gap-x-8 items-center lg:gap-x-14 md:flex-row md:items-center">
                    <!-- Image -->
                    <div class="relative flex-shrink-0 w-full max-w-xs transform md:w-1/3"
                        :class="block.rotationClass || (index % 2 === 0 ? '-rotate-2' : 'rotate-2')">
                        <div class="">
                            <nuxt-img format="webp" sizes="xs:170px sm:170px md:170px lg:170px xl:251px"
                                :src="block.image" :alt="block.title" :width="block.imageWidth || 300"
                                :height="block.imageHeight || 215" class="" loading="lazy" />
                        </div>
                    </div>
                    <!-- Content -->
                    <div class="flex-1 text-left">
                        <h3 :class="newStyle ? 'flex gap-3 items-center lg:mb-3 mb-1 text-2xl font-bold text-gray-900 justify-start md:text-xl xl:text-2xl font-ww-bold' : 'flex gap-3 justify-center items-center mb-3 text-lg font-bold text-gray-900 md:justify-start md:text-xl xl:text-2xl font-ww-bold'"
                            class="">
                            <span class="text-2xl">{{ block.emoji }}</span> <span v-html="block.title"></span>
                        </h3>
                        <p class="text-lg xl:text-23 md:text-xl font-ww-regular"
                            :class="newStyle ? 'text-xl xl:text-23 md:text-xl font-ww-regular' : 'text-lg xl:text-23 md:text-xl font-ww-regular'"
                            v-html="block.text">
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    newStyle: { type: Boolean, default: false },
    mainTitle: { type: String, default: 'Why Bulk Buying Benefits Everyone' },
    blocks: {
        type: Array,
        default: () => [
            {
                emoji: '💰',
                title: 'You Save More',
                text: 'We get volume discounts from our farmers and roasters—and we pass 100% of those savings to you. Plus, shipping a single bag costs us over $10, but bulk orders dramatically reduce per-bag shipping costs.',
                image: '/assets/images/save-more.png',
                imageWidth: 301,
                imageHeight: 214,
                rotationClass: '-rotate-2'
            },
            {
                emoji: '🌍',
                title: 'Better for the Planet',
                text: 'Bulk orders mean less cardboard, less packaging material, and fewer delivery trips. Instead of thousands of single-bag shipments per year, we consolidate—reducing our carbon footprint and helping protect the environment.',
                image: '/assets/images/better-planet.png',
                imageWidth: 296,
                imageHeight: 214,
                rotationClass: 'rotate-2'
            },
            {
                emoji: '📦',
                title: 'Smart Economics',
                text: 'When you buy in bulk, everyone wins: you get premium coffee at lower prices, we operate more efficiently, and the planet benefits from reduced waste and emissions.',
                image: '/assets/images/smart-economics.png',
                imageWidth: 297,
                imageHeight: 208,
                rotationClass: '-rotate-1'
            }
        ]
    }
});

defineBuilderComponent({
    friendlyName: 'Media List Block',
    inputs: [
        { name: 'newStyle', type: 'boolean', defaultValue: false, friendlyName: 'New Style' },
        { name: 'mainTitle', type: 'text', defaultValue: 'Why Bulk Buying Benefits Everyone', friendlyName: 'Main Title' },
        {
            name: 'blocks',
            type: 'list',
            subFields: [
                { name: 'emoji', type: 'text', defaultValue: '💰', friendlyName: 'Emoji' },
                { name: 'title', type: 'text', defaultValue: 'Title', friendlyName: 'Title' },
                { name: 'text', type: 'longText', defaultValue: 'Description text...', friendlyName: 'Description' },
                { name: 'image', type: 'file', allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'], defaultValue: '', friendlyName: 'Image' },
                { name: 'imageWidth', type: 'number', defaultValue: 300, friendlyName: 'Image Width' },
                { name: 'imageHeight', type: 'number', defaultValue: 215, friendlyName: 'Image Height' },
                { name: 'rotationClass', type: 'text', defaultValue: '-rotate-2', helperText: 'e.g. rotate-2, -rotate-2', friendlyName: 'Rotation Class' }
            ],
            defaultValue: [
                {
                    emoji: '💰',
                    title: 'You Save More',
                    text: 'We get volume discounts from our farmers and roasters—and we pass 100% of those savings to you. Plus, shipping a single bag costs us over $10, but bulk orders dramatically reduce per-bag shipping costs.',
                    image: '/assets/images/save-more.png',
                    imageWidth: 301,
                    imageHeight: 214,
                    rotationClass: '-rotate-2'
                },
                {
                    emoji: '🌍',
                    title: 'Better for the Planet',
                    text: 'Bulk orders mean less cardboard, less packaging material, and fewer delivery trips. Instead of thousands of single-bag shipments per year, we consolidate—reducing our carbon footprint and helping protect the environment.',
                    image: '/assets/images/better-planet.png',
                    imageWidth: 296,
                    imageHeight: 214,
                    rotationClass: 'rotate-2'
                },
                {
                    emoji: '📦',
                    title: 'Smart Economics',
                    text: 'When you buy in bulk, everyone wins: you get premium coffee at lower prices, we operate more efficiently, and the planet benefits from reduced waste and emissions.',
                    image: '/assets/images/smart-economics.png',
                    imageWidth: 297,
                    imageHeight: 208,
                    rotationClass: '-rotate-1'
                }
            ]
        }
    ]
})
</script>

<style scoped>
/* Add any specific component styles here if Tailwind is not enough */
</style>
